import { isEditorHtml } from './editorHtml';

const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&nbsp;': ' ',
  '&quot;': '"',
  '&#39;': "'",
};

const SCRIPT_OR_STYLE_BLOCK = /<script[\s\S]*?<\/script>|<style[\s\S]*?<\/style>/gi;
const INLINE_TAG = /<\/?(b|strong|i|em|u|s|a)(\s[^>]*)?\/?>/gi;
const BLOCK_TAG = /<\/?(p|br|li|ul|ol|h[1-6]|pre)(\s[^>]*)?\/?>/gi;

/**
 * DOM-free plain-text projection of a `RichTextArea` value, for client-side
 * keyword search. Strips tags and decodes the small set of entities DOMPurify
 * output can contain, then collapses whitespace. Tag stripping is gated on
 * `isEditorHtml` -- legacy plain-text values (no tags, or a bare opening tag
 * that merely spells out a supported tag name, e.g. "Use <pre> for
 * examples") pass through unchanged aside from whitespace collapsing, so the
 * token still searches verbatim rather than being stripped as if it were
 * markup. Inline formatting tags (`b`/`strong`/`i`/`em`/`u`/`s`/`a`) are
 * removed with no replacement so `on-<strong>call</strong>` still reads as
 * `on-call`; block/line tags are replaced with a space so adjacent
 * paragraphs/list items don't run together. `<script>`/`<style>` content is
 * dropped entirely (defense in depth -- stored values are already
 * DOMPurify-sanitized).
 */
export const stripHtml = (value: string | null | undefined): string => {
  if (!value) return '';
  const withTagsStripped = isEditorHtml(value)
    ? value.replace(SCRIPT_OR_STYLE_BLOCK, ' ').replace(INLINE_TAG, '').replace(BLOCK_TAG, ' ')
    : value;
  return withTagsStripped
    .replace(/&(amp|lt|gt|nbsp|quot|#39);/g, (match) => HTML_ENTITIES[match] ?? match)
    .replace(/\s+/g, ' ')
    .trim();
};
